import React, { useId } from 'react'

import clsx from 'clsx'
import './ApplyV2Switch.scss'

interface Props {
    checked: boolean
    label?: string
    onChange: React.ChangeEventHandler<HTMLInputElement>
    onFocus?: React.FocusEventHandler<HTMLInputElement>
    onBlur?: React.FocusEventHandler<HTMLInputElement>
    isDisabled?: boolean
    className?: string
    id?: string | number
    name?: string
    isLoading?: boolean
    errorMessage?: string | null
    children?: React.ReactNode
    isShowErrorMessage?: boolean
}

const baseClass = 'ApplyV2Switch'

function ApplyV2Switch({
    checked,
    label = '',
    onChange,
    children,
    onBlur = () => {},
    onFocus = () => {},
    isDisabled = false,
    className = '',
    id,
    name,
    isLoading = false,
    errorMessage,
    isShowErrorMessage = true,
}: Props) {
    const defaultId = useId()
    const htmlForId = id ? id.toString() : defaultId

    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--error`]: !!errorMessage,
                [`${baseClass}--disabled`]: isDisabled,
                [`${baseClass}--loading`]: isLoading,
            })}
        >
            <label
                htmlFor={htmlForId}
                className={clsx(`${baseClass}__label`, {
                    [`${className}__label`]: !!className,
                    [`${baseClass}__label--disabled`]: isDisabled,
                    [`${baseClass}__label--checked`]: checked,
                    [`${baseClass}__label--loading`]: isLoading,
                    [`${baseClass}__label--error`]: !!errorMessage,
                    [`${className}__label--disabled`]: className && isDisabled,
                    [`${className}__label--checked`]: className && checked,
                    [`${className}__label--loading`]: className && isLoading,
                    [`${className}__label--error`]: className && !!errorMessage,
                })}
            >
                <input
                    className={clsx(`${baseClass}__input`, {
                        [`${baseClass}__input--disabled`]: isDisabled,
                        [`${baseClass}__input--loading`]: isLoading,
                        [`${baseClass}__input--error`]: !!errorMessage,
                        [`${baseClass}__input--checked`]: checked,
                        [`${className}__input--disabled`]: className && isDisabled,
                        [`${className}__input--loading`]: className && isLoading,
                        [`${className}__input--error`]: className && !!errorMessage,
                        [`${className}__input--checked`]: className && checked,
                    })}
                    type="checkbox"
                    role="switch"
                    aria-checked={checked}
                    checked={checked}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    disabled={isDisabled}
                    id={htmlForId}
                    name={name}
                />

                <div
                    className={clsx(`${baseClass}__slider`, {
                        [`${baseClass}__slider--disabled`]: isDisabled,
                        [`${baseClass}__slider--loading`]: isLoading,
                        [`${baseClass}__slider--error`]: !!errorMessage,
                        [`${baseClass}__slider--checked`]: checked,
                        [`${className}__slider--disabled`]: className && isDisabled,
                        [`${className}__slider--loading`]: className && isLoading,
                        [`${className}__slider--error`]: className && !!errorMessage,
                        [`${className}__slider--checked`]: className && checked,
                    })}
                />
                {(children || label) && (
                    <div
                        className={clsx(`${baseClass}__caption`, {
                            [`${baseClass}__caption--disabled`]: isDisabled,
                            [`${baseClass}__caption--loading`]: isLoading,
                            [`${baseClass}__caption--error`]: !!errorMessage,
                            [`${className}__caption--disabled`]: className && isDisabled,
                            [`${className}__caption--loading`]: className && isLoading,
                            [`${className}__caption--error`]: className && !!errorMessage,
                        })}
                    >
                        {children || label}
                    </div>
                )}
            </label>

            {errorMessage && isShowErrorMessage && (
                <div className={`${baseClass}__error`}>
                    <div className={`${baseClass}__error--icon`}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_4800_38853)">
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8.00006 1.9334C4.64954 1.9334 1.9334 4.64954 1.9334 8.00006C1.9334 11.3506 4.64954 14.0667 8.00006 14.0667C11.3506 14.0667 14.0667 11.3506 14.0667 8.00006C14.0667 4.64954 11.3506 1.9334 8.00006 1.9334ZM0.733398 8.00006C0.733398 3.9868 3.9868 0.733398 8.00006 0.733398C12.0133 0.733398 15.2667 3.9868 15.2667 8.00006C15.2667 12.0133 12.0133 15.2667 8.00006 15.2667C3.9868 15.2667 0.733398 12.0133 0.733398 8.00006ZM8.00006 9.26673C7.66869 9.26673 7.40007 8.9981 7.40007 8.66673V4.66673C7.40007 4.33536 7.66869 4.06673 8.00006 4.06673C8.33144 4.06673 8.60006 4.33536 8.60006 4.66673V8.66673C8.60006 8.9981 8.33144 9.26673 8.00006 9.26673ZM8.00006 11.3334C8.36826 11.3334 8.66673 11.0349 8.66673 10.6667C8.66673 10.2985 8.36826 10.0001 8.00006 10.0001C7.63188 10.0001 7.3334 10.2985 7.3334 10.6667C7.3334 11.0349 7.63188 11.3334 8.00006 11.3334Z"
                                    fill="#B62626"
                                />
                            </g>
                            <defs>
                                <clipPath id="clip0_4800_38853">
                                    <rect width="16" height="16" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </div>
                    <div className={`${baseClass}__error--text`}>{errorMessage}</div>
                </div>
            )}
        </div>
    )
}

export default ApplyV2Switch
